import Control from "ol/control/Control";
import { selectWidthPx } from "./selectWidth.mjs";
import {
  installEntryEvents,
  releaseEntries,
  renderSelectedFeatures,
} from "../featureinfo/entryGlue.js";

export class VectorFeatureControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    const div = document.createElement("div");
    div.className = "vector-feature-layer-row";
    super({
      element: div,
      target: options.target,
    });
    const this_ = this;
    const random = Math.floor(Math.random() * 10000);

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "vector-feature-checkbox";
    checkbox.id = "vector-feature-input-" + random;
    checkbox.checked = false;

    checkbox.addEventListener("change", function () {
      this_.set("vectorToggle", checkbox.checked);
    });

    this._checkbox = checkbox;

    const label = document.createElement("label");
    label.htmlFor = checkbox.id;
    label.className = "vector-feature-text";
    label.innerText = options.layer_name;

    const statusIndicator = document.createElement("span");
    statusIndicator.className = "vector-feature-status";

    // Optional view dropdown: rows whose flavours share one WFS (`options.views`,
    // [{ id, label }]) switch style through `view` instead of being separate rows.
    let viewSelect = null;
    if (options.views) {
      viewSelect = document.createElement("select");
      viewSelect.className = "vector-feature-view-select";
      viewSelect.title = "View";
      options.views.forEach(({ id, label }) => {
        viewSelect.appendChild(new Option(label, id));
      });
      viewSelect.addEventListener("change", function () {
        this_.set("view", viewSelect.value);
        fitViewSelect();
      });
      // Re-measure once the web font is in: the first measurement may use the fallback.
      document.fonts?.ready.then(() => fitViewSelect());
    }

    // The closed dropdown is as wide as the selected text, right-aligned in the
    // header (margin-left: auto); the open list stays the browser's own.
    let measureContext;
    function fitViewSelect() {
      if (!viewSelect || !viewSelect.isConnected) return;
      const option = viewSelect.options[viewSelect.selectedIndex];
      if (!option) return;
      const style = getComputedStyle(viewSelect);
      measureContext ??= document.createElement("canvas").getContext("2d");
      measureContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const width = measureContext.measureText(option.text).width;
      viewSelect.style.width = `${selectWidthPx(width, style)}px`;
    }

    const errorMessage = document.createElement("div");
    errorMessage.className = "vector-feature-error-message";

    const attributes = document.createElement("table");
    attributes.className = "ol-selectable vector-feature-attributes-list";
    installEntryEvents(attributes);
    // The selection the attributes were last built from: entries are rebuilt only
    // when it changes, not on every loading/active toggle (keeps open expands open).
    let renderedSelection;

    // Header line: checkbox + label (+ revision icon appended by RevisionControl)
    const header = document.createElement("div");
    header.className = "vector-feature-header";

    div.appendChild(header);
    header.appendChild(checkbox);
    header.appendChild(label);
    header.appendChild(statusIndicator);
    if (viewSelect) header.appendChild(viewSelect);
    div.appendChild(errorMessage);
    div.append(attributes);

    // Expose so RevisionControl can append its icon into the header
    // and its panel between the header and the attributes table.
    this.headerEl = header;
    this.bodyEl = div;

    this.on("propertychange", () => {
      // roles
      if (this_.get("enabled") == false) {
        div.style.display = "none";
        checkbox.checked = false;
      } else {
        div.style.display = "block";
      }

      // loading / error status
      if (this_.get("loading") === true) {
        statusIndicator.className =
          "vector-feature-status vector-feature-status--loading";
        statusIndicator.title = "Loading…";
        errorMessage.textContent = "";
        errorMessage.style.display = "none";
      } else if (this_.get("loadError") === true) {
        statusIndicator.className =
          "vector-feature-status vector-feature-status--error";
        statusIndicator.title = "Loading failed";
        const reason = this_.get("loadErrorReason");
        errorMessage.textContent = reason
          ? `Failed to load: ${reason}`
          : "Failed to load";
        errorMessage.style.display = "block";
      } else {
        statusIndicator.className = "vector-feature-status";
        statusIndicator.title = "";
        errorMessage.textContent = "";
        errorMessage.style.display = "none";
      }

      // attributes
      if (this_.get("vectorToggle") == false) {
        // unset selected feature if disabled
        this_.set("selectedFeatures", undefined);
      }
      const selectedFeatures = this_.get("selectedFeatures");
      if (selectedFeatures !== renderedSelection) {
        renderedSelection = selectedFeatures;
        attributes.replaceChildren();
        const colGroup = document.createElement("colgroup");
        const colLabel = document.createElement("col");
        colLabel.classList.add("label");
        const colValue = document.createElement("col");
        colValue.classList.add("value");
        colGroup.appendChild(colLabel);
        colGroup.appendChild(colValue);
        attributes.appendChild(colGroup);
        if (selectedFeatures !== undefined) {
          attributes.style = "display: table;";
          const sorted = Array.from(selectedFeatures).sort((f1, f2) => {
            if (f2.get(options.sort_field)) {
              return f2
                .get(options.sort_field)
                .localeCompare(f1.get(options.sort_field));
            }
          });
          renderSelectedFeatures(this_, sorted, {
            rowName: options.layer_name,
            headerField: options.header_field,
            source: this_.layer?.getSource(),
          }).forEach((entry) => attributes.append(entry));
        } else {
          attributes.style = "display: block;";
          releaseEntries(this_);
        }
      }

      // sync checkbox with vectorToggle property
      checkbox.checked = !!this_.get("vectorToggle");
      const view = this_.get("view");
      if (viewSelect && view && viewSelect.value !== view) {
        viewSelect.value = view;
      }
      // Cheap, and the select is only in the document once the control is placed.
      fitViewSelect();

      // active
      if (this_.get("active")) {
        div.classList.add("layer-active");
        div.classList.remove("layer-inactive");
      } else {
        div.classList.remove("layer-active");
        div.classList.add("layer-inactive");
      }
    });
  }

  setToggle(value) {
    this.set("vectorToggle", value);
  }
}
